import type { SupabaseClient, User } from "@supabase/supabase-js";

// Reads the best display name from a Google identity's metadata.
export function displayName(user: User): string | null {
  const m = user.user_metadata ?? {};
  return (m.full_name as string) ?? (m.name as string) ?? user.email ?? null;
}

// Reads the Google avatar URL from identity metadata, if present.
export function avatarUrl(user: User): string | null {
  const m = user.user_metadata ?? {};
  return (m.avatar_url as string) ?? (m.picture as string) ?? null;
}

// Ensures a profiles row exists for the signed-in user. Called once from the
// /auth/callback route after the session is established. Insert-if-missing
// (ignoreDuplicates) so a later user-edited name isn't clobbered on re-login.
// RLS policy `own_profile` permits this because user_id === auth.uid().
export async function ensureProfile(
  supabase: SupabaseClient,
  user: User,
): Promise<void> {
  const { error } = await supabase
    .from("profiles")
    .upsert(
      { user_id: user.id, name: displayName(user) },
      { onConflict: "user_id", ignoreDuplicates: true },
    );
  if (error) {
    // Non-fatal: the row may already exist or RLS/no-op. Log and continue so a
    // transient profile write never blocks the user from entering the app.
    console.warn("ensureProfile:", error.message);
  }
}
